/**
 * 管理后台 - 积分配置管理页面
 * 
 * 功能:
 * 1. 查看和编辑积分系统全局配置
 * 2. 配置购课返利比例、抵扣基准、风控限额等
 * 3. 配置各类积分有效期规则
 */

import React, { useEffect, useState } from 'react';
import { supabase } from '../../supabase/client';

interface Setting {
  id: string;
  setting_key: string;
  setting_value: any;
  description: string;
}

interface ExpirationRule {
  id: string;
  point_type: string;
  validity_days: number;
  expiration_method: string;
  description: string;
  is_active: boolean;
}

const POINT_TYPE_LABELS: Record<string, string> = {
  purchase_rebate: '购课返利',
  error_correction: '纠错奖励',
  activity_reward: '活动奖励',
  teacher_gift: '教师赠送',
};

const PointSettings: React.FC = () => {
  const [settings, setSettings] = useState<Setting[]>([]);
  const [expirationRules, setExpirationRules] = useState<ExpirationRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // 获取配置
      const { data: settingsData } = await supabase
        .from('point_settings')
        .select('*')
        .order('setting_key');

      if (settingsData) {
        setSettings(settingsData);
      }

      // 获取有效期规则
      const { data: rulesData } = await supabase
        .from('point_expiration_rules')
        .select('*')
        .order('point_type');

      if (rulesData) {
        setExpirationRules(rulesData);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSetting = async (key: string, value: any) => {
    try {
      setSaving(true);
      
      const { error } = await supabase
        .from('point_settings')
        .update({ setting_value: value })
        .eq('setting_key', key);

      if (error) {
        throw error;
      }

      alert('配置已更新');
      fetchData();
    } catch (error) {
      console.error('Failed to update setting:', error);
      alert('更新失败');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRule = async (ruleId: string, updates: Partial<ExpirationRule>) => {
    try {
      setSaving(true);
      
      const { error } = await supabase
        .from('point_expiration_rules')
        .update(updates)
        .eq('id', ruleId);

      if (error) {
        throw error;
      }

      alert('规则已更新');
      fetchData();
    } catch (error) {
      console.error('Failed to update rule:', error);
      alert('更新失败');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-500">加载中...</div>;
  }

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">积分配置管理</h1>
        <p className="text-sm text-gray-500 mt-1">管理积分系统的全局配置和有效期规则</p>
      </div>

      {/* 全局配置 */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">全局配置</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {settings.map((setting) => (
            <SettingCard
              key={setting.id}
              setting={setting}
              onUpdate={handleUpdateSetting}
              disabled={saving}
            />
          ))}
        </div>
      </div>

      {/* 有效期规则 */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">积分有效期规则</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-700">积分类型</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">有效期(天)</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">过期方式</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">状态</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">操作</th>
              </tr>
            </thead>
            <tbody>
              {expirationRules.map((rule) => (
                <tr key={rule.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4">{POINT_TYPE_LABELS[rule.point_type] || rule.point_type}</td>
                  <td className="py-3 px-4">
                    <input
                      type="number"
                      value={rule.validity_days}
                      onChange={(e) => {
                        const newRules = expirationRules.map(r =>
                          r.id === rule.id ? { ...r, validity_days: parseInt(e.target.value) || 0 } : r
                        );
                        setExpirationRules(newRules);
                      }}
                      onBlur={() => handleUpdateRule(rule.id, { validity_days: rule.validity_days })}
                      className="w-20 px-2 py-1 border border-gray-300 rounded text-center"
                      disabled={saving}
                    />
                  </td>
                  <td className="py-3 px-4">
                    <select
                      value={rule.expiration_method}
                      onChange={(e) => handleUpdateRule(rule.id, { expiration_method: e.target.value })}
                      className="px-2 py-1 border border-gray-300 rounded"
                      disabled={saving}
                    >
                      <option value="fifo">FIFO (先进先出)</option>
                      <option value="lifo">LIFO (后进先出)</option>
                    </select>
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                        rule.is_active
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {rule.is_active ? '启用' : '禁用'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <button
                      onClick={() => handleUpdateRule(rule.id, { is_active: !rule.is_active })}
                      className={`text-xs px-3 py-1 rounded ${
                        rule.is_active
                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                      disabled={saving}
                    >
                      {rule.is_active ? '禁用' : '启用'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// 配置卡片组件
const SettingCard: React.FC<{
  setting: Setting;
  onUpdate: (key: string, value: any) => void;
  disabled: boolean;
}> = ({ setting, onUpdate, disabled }) => {
  const renderInput = () => {
    const value = setting.setting_value;

    switch (setting.setting_key) {
      case 'purchase_rebate_base':
        return (
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={value.value || 5}
              onChange={(e) => onUpdate(setting.setting_key, { type: 'percentage', value: parseInt(e.target.value) || 0 })}
              className="w-20 px-3 py-2 border border-gray-300 rounded-lg"
              disabled={disabled}
            />
            <span className="text-gray-500">%</span>
          </div>
        );
      case 'deduction_base':
        return (
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={value.points_per_yuan || 100}
              onChange={(e) => onUpdate(setting.setting_key, { type: 'ratio', points_per_yuan: parseInt(e.target.value) || 100 })}
              className="w-20 px-3 py-2 border border-gray-300 rounded-lg"
              disabled={disabled}
            />
            <span className="text-gray-500">积分 = 1元</span>
          </div>
        );
      case 'risk_control_daily_max':
        return (
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={value.max_points || 10000}
              onChange={(e) => onUpdate(setting.setting_key, { max_points: parseInt(e.target.value) || 0 })}
              className="w-24 px-3 py-2 border border-gray-300 rounded-lg"
              disabled={disabled}
            />
            <span className="text-gray-500">分/天</span>
          </div>
        );
      case 'error_correction_reward':
        return (
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={value.points || 50}
              onChange={(e) => onUpdate(setting.setting_key, { points: parseInt(e.target.value) || 0 })}
              className="w-20 px-3 py-2 border border-gray-300 rounded-lg"
              disabled={disabled}
            />
            <span className="text-gray-500">分/处</span>
          </div>
        );
      case 'activity_reward_default':
        return (
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={value.points || 100}
              onChange={(e) => onUpdate(setting.setting_key, { points: parseInt(e.target.value) || 0 })}
              className="w-20 px-3 py-2 border border-gray-300 rounded-lg"
              disabled={disabled}
            />
            <span className="text-gray-500">分</span>
          </div>
        );
      case 'teacher_gift_monthly_limit':
        return (
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={value.max_points || 5000}
              onChange={(e) => onUpdate(setting.setting_key, { max_points: parseInt(e.target.value) || 0 })}
              className="w-24 px-3 py-2 border border-gray-300 rounded-lg"
              disabled={disabled}
            />
            <span className="text-gray-500">分/月</span>
          </div>
        );
      default:
        return <span className="text-gray-400">未知配置类型</span>;
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="text-sm font-medium text-gray-700 mb-2">{setting.description}</div>
      <div className="text-xs text-gray-400 mb-3">Key: {setting.setting_key}</div>
      {renderInput()}
    </div>
  );
};

export default PointSettings;
